import type { ClientPlugin, QueryResult } from '$houdini';
import { env } from '$env/dynamic/public';
import { HoudiniClient } from '$houdini';
import { getAuthStore } from '$lib/auth/store';
import { GraphQLError } from '$lib/errors/graphql';
import { getPlatform } from '$lib/utils/getPlatform';
import { error } from '@sveltejs/kit';

const { PUBLIC_BFF_URL = 'https://bff.viamichelin.com/' } = env;

function isAuthError(queryResult: QueryResult) {
	// @ts-expect-error extensions may exist on error
	return queryResult.errors?.some(error => error.extensions?.code === 'INVALID_AUTH_HEADER');
}

const retryOnExpiredToken: ClientPlugin = () => {
	return {
		async end(ctx, { value, next, resolve }) {
			if (isAuthError(value) && !ctx.stuff?.retry) {
				const authStore = getAuthStore();

				const newUserSession = await authStore?.refreshSession();

				if (!newUserSession) {
					// TODO: What should we do if the user is diconnected ?
					// For now just do nothing...

					resolve(ctx);
					return;
				}

				// Use only the network cache and not the Houdini cache
				// We want the request to trigger again
				ctx.policy = 'NetworkOnly';

				ctx.stuff = {
					...ctx.stuff,
					retry: true,
				};
				next(ctx);
				return;
			}

			resolve(ctx);
		},
	};
};

export default new HoudiniClient({
	url: `${PUBLIC_BFF_URL}/graphql`,
	throwOnError: {
		operations: ['all'],
		error: (errors, ctx) => {
			const extensionCode = (errors[0] as any).extensions?.code;
			if (extensionCode === 'POI_ID_NOT_FOUND') {
				error(410);
			} else if (extensionCode?.endsWith('NOT_FOUND')) {
				error(404);
			}

			throw new GraphQLError(
				`(${ctx.artifact.name}): ${extensionCode ?? ''} ${errors[0].message}`,
				extensionCode,
			);
		},
	},
	plugins: [retryOnExpiredToken],
	fetchParams({ session, metadata }): { headers: Record<string, string> } {
		const locale = metadata?.locale ?? session?.locale ?? 'fr-FR';
		const userSession = getAuthStore()?.getSession();
		const platform = getPlatform();
		const defaultHeaders = {
			language: locale,
			platform,
		};

		if (!userSession?.id_token) {
			return {
				headers: defaultHeaders,
			};
		}

		const { id_token } = userSession;

		return {
			headers: {
				Authorization: `Bearer ${id_token}`,
				...defaultHeaders,
			},
		};
	},
});
